import { ReactNode, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { clearLoggedOut, isLoggedOut } from '../hooks/logoutState'
import { LoggedOut } from './LoggedOut'

interface RequireAuthProps {
  children: ReactNode
}

const LoadingSpinner = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="48"
    height="48"
    style={{ animation: 'spin 1s linear infinite' }}
  >
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
  </svg>
)

/**
 * Check if the URL contains OAuth callback parameters (code + state).
 * When present, the IdP SDK is processing the callback - don't redirect to login.
 */
function isOAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.has('code') && params.has('state')
}

/**
 * Wrapper component that requires authentication.
 * Automatically redirects to the provider's login if user is not authenticated.
 * After successful login, redirects back to the originally requested URL.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isLoading, isAuthenticated, isEnabled, loginWithRedirect } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const hasCheckedReturnTo = useRef(false)

  // Auto-redirect to the provider's login when not authenticated
  // Pass current path so we can return after login
  // IMPORTANT: Skip redirect while a callback is being processed (code+state in URL)
  useEffect(() => {
    if (!isLoading && isEnabled && !isAuthenticated && !isLoggedOut() && !isOAuthCallback()) {
      const returnTo = location.pathname + location.search
      loginWithRedirect({ returnTo })
    }
  }, [isLoading, isEnabled, isAuthenticated, loginWithRedirect, location])

  // After authentication completes, navigate to the stored return path
  useEffect(() => {
    if (isAuthenticated && !hasCheckedReturnTo.current) {
      hasCheckedReturnTo.current = true
      const returnTo = sessionStorage.getItem('auth_return_to')
      if (returnTo && returnTo !== location.pathname) {
        sessionStorage.removeItem('auth_return_to')
        navigate(returnTo, { replace: true })
      }
    }
  }, [isAuthenticated, navigate, location.pathname])

  // No provider configured - allow access (dev server only; main.tsx fails closed otherwise)
  if (!isEnabled) {
    return <>{children}</>
  }

  // Just logged out: land here instead of auto-redirecting back into SSO.
  // "Sign in again" clears the flag and re-enters the provider's login flow.
  if (!isAuthenticated && isLoggedOut()) {
    return (
      <LoggedOut
        onSignIn={() => {
          clearLoggedOut()
          loginWithRedirect({ returnTo: location.pathname + location.search })
        }}
      />
    )
  }

  // Still loading auth state or redirecting to login
  if (isLoading || !isAuthenticated) {
    // Determine the loading message based on state
    const isCallback = isOAuthCallback()
    let message = 'Redirecting to login...'
    if (isLoading) {
      message = 'Checking authentication...'
    } else if (isCallback) {
      message = 'Completing login...'
    }

    return (
      <div className="auth-loading">
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .auth-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            width: 100vw;
            position: fixed;
            top: 0;
            left: 0;
            background: var(--color-bg-primary);
            color: var(--color-text-secondary);
            gap: 24px;
            z-index: 9999;
          }
          .auth-loading svg {
            color: var(--color-accent);
          }
          .auth-loading p {
            font-size: 14px;
            margin: 0;
          }
        `}</style>
        <LoadingSpinner />
        <p>{message}</p>
      </div>
    )
  }

  // Authenticated - render children
  return <>{children}</>
}

export default RequireAuth
